import { isEvidenceProfileEnabled } from '@/config/evidenceProfile';
import type { AnalyticsConfig } from './analyticsConfig';
import type { ChartableColumn } from './chartableColumns';
import { getChartableByField, resolveColumnChartTitle } from './chartableColumns';
import { getDefaultColumnChartType, type ColumnChartType } from './columnChartTypes';
import type { MetricDefinition } from './metricCatalog';
import { getMetricByField } from './metricCatalog';

export type PanelWidgetType =
  | 'topEntities'
  | 'publicationTrend'
  | 'scoreDistribution'
  | 'metricScatter'
  | 'evidenceProfile'
  | 'columnChart';

export type WidgetSpan = 'wide' | 'half' | 'third';

export interface TopEntitiesParams {
  metric: string;
}

export interface ScoreDistributionParams {
  metric: string;
}

export type ScatterPanelChartType = 'scatter' | 'horizontalBar' | 'verticalBar';

export interface MetricScatterParams {
  xMetric: string;
  yMetric: string;
  chartType?: ScatterPanelChartType;
}

export interface ColumnChartParams {
  field: string;
  chartType?: ColumnChartType;
}

export type PanelParams =
  | TopEntitiesParams
  | ScoreDistributionParams
  | MetricScatterParams
  | ColumnChartParams
  | Record<string, never>;

export interface PanelWidgetInstance {
  id: string;
  type: PanelWidgetType;
  hidden: boolean;
  params: PanelParams;
}

export interface SummaryCardInstance {
  id: string;
  hidden: boolean;
  metric?: string;
}

export interface DashboardLayoutState {
  version: 1 | 2;
  cards: SummaryCardInstance[];
  panels: PanelWidgetInstance[];
  dismissedPresets?: PanelWidgetType[];
}

export const MAX_COLUMN_CHARTS = 9;

export function isColumnChartPanel(panel: PanelWidgetInstance): boolean {
  return panel.type === 'columnChart';
}

export function isPresetPanelType(type: PanelWidgetType): boolean {
  return type !== 'columnChart';
}

export function buildColumnChartId(field: string): string {
  return `columnChart:${field}`;
}

export function createColumnChartPanel(column: ChartableColumn): PanelWidgetInstance {
  return {
    id: buildColumnChartId(column.field),
    type: 'columnChart',
    hidden: false,
    params: {
      field: column.field,
      chartType: getDefaultColumnChartType(column.kind),
    },
  };
}

export interface PanelDefinition {
  type: PanelWidgetType;
  label: string;
  span: WidgetSpan;
  isAvailable: (config: AnalyticsConfig) => boolean;
  defaultParams: (config: AnalyticsConfig, catalog: MetricDefinition[]) => PanelParams;
}

const PANEL_DEFINITIONS: Record<PanelWidgetType, PanelDefinition> = {
  topEntities: {
    type: 'topEntities',
    label: 'Top entities',
    span: 'wide',
    isAvailable: () => true,
    defaultParams: (config): TopEntitiesParams => ({
      metric: config.fields.score,
    }),
  },
  publicationTrend: {
    type: 'publicationTrend',
    label: 'Cumulative publications by year',
    span: 'half',
    isAvailable: config => Boolean(config.features.publicationTrend && config.fields.yearFinal),
    defaultParams: () => ({}),
  },
  scoreDistribution: {
    type: 'scoreDistribution',
    label: 'Score distribution',
    span: 'half',
    isAvailable: () => true,
    defaultParams: (config): ScoreDistributionParams => ({
      metric: config.fields.score,
    }),
  },
  metricScatter: {
    type: 'metricScatter',
    label: 'Metric vs score',
    span: 'wide',
    isAvailable: config => Boolean(config.features.metricScatter && config.scatterTabs.length),
    defaultParams: (config): MetricScatterParams => {
      const first = config.scatterTabs[0];

      if (config.tab === 'DDA') {
        return {
          xMetric: config.fields.score,
          yMetric: config.fields.score,
          chartType: 'horizontalBar',
        };
      }
      return {
        xMetric: first?.metricKey ?? config.fields.ei,
        yMetric: config.fields.score,
      };
    },
  },
  evidenceProfile: {
    type: 'evidenceProfile',
    label: 'Evidence profile',
    span: 'wide',
    isAvailable: config => isEvidenceProfileEnabled && config.tab !== 'DDA',
    defaultParams: () => ({}),
  },
  columnChart: {
    type: 'columnChart',
    label: 'Column chart',
    span: 'third',
    isAvailable: () => true,
    defaultParams: (): ColumnChartParams => ({ field: '', chartType: 'verticalBar' }),
  },
};

export function getPanelDefinition(type: PanelWidgetType): PanelDefinition {
  return PANEL_DEFINITIONS[type];
}

export function getDefaultPanelOrder(config: AnalyticsConfig): PanelWidgetType[] {
  const order: PanelWidgetType[] = ['topEntities'];
  if (PANEL_DEFINITIONS.publicationTrend.isAvailable(config)) {
    order.push('publicationTrend');
  }
  order.push('scoreDistribution');
  if (PANEL_DEFINITIONS.metricScatter.isAvailable(config)) {
    order.push('metricScatter');
  }
  if (PANEL_DEFINITIONS.evidenceProfile.isAvailable(config)) {
    order.push('evidenceProfile');
  }
  return order;
}

export function buildDefaultLayout(
  config: AnalyticsConfig,
  catalog: MetricDefinition[],
): DashboardLayoutState {
  const panels = getDefaultPanelOrder(config).map(type => {
    const definition = PANEL_DEFINITIONS[type];
    return {
      id: type,
      type,
      hidden: false,
      params: definition.defaultParams(config, catalog),
    };
  });

  const cards = config.summaryCards.map(card => ({
    id: card.id,
    hidden: false,
  }));

  return { version: 2, cards, panels };
}

export function getPanelLabel(
  type: PanelWidgetType,
  config: AnalyticsConfig,
  options?: { column?: ChartableColumn; field?: string; chartableColumns?: ChartableColumn[] },
): string {
  if (type === 'topEntities') return config.topBarTitle;
  if (type === 'metricScatter') return config.topScatterTitle;
  if (type === 'columnChart') {
    const column = options?.column ?? getChartableByField(options?.chartableColumns ?? [], options?.field);
    if (column) return resolveColumnChartTitle(column);
    return PANEL_DEFINITIONS.columnChart.label;
  }
  return PANEL_DEFINITIONS[type].label;
}

export function resolveTopEntitiesTitle(
  config: AnalyticsConfig,
  metric: MetricDefinition | undefined,
): string {
  if (!metric || metric.field === config.fields.score) {
    return config.topBarTitle;
  }
  const entity = config.labels.entityAxis.toLowerCase();
  const plural = entity.endsWith('s') ? entity : `${entity}s`;
  return `Top 15 ${plural} by ${metric.label.toLowerCase()}`;
}

export function resolveScatterTitle(
  config: AnalyticsConfig,
  xMetric: MetricDefinition | undefined,
  yMetric: MetricDefinition | undefined,
): string {
  const defaultX = config.scatterTabs[0]?.metricKey;
  if ((!xMetric || xMetric.field === defaultX) && (!yMetric || yMetric.field === config.fields.score)) {
    return config.topScatterTitle;
  }
  const xLabel = xMetric?.label ?? 'Metric';
  const yLabel = yMetric?.label ?? config.labels.scatterY;
  return `${xLabel} vs ${yLabel}`;
}

export function isPresetSummaryCard(cardId: string): boolean {
  return cardId === 'associated' || cardId === 'pairs' || cardId === 'active-pmids';
}

export function getCardLabel(
  cardId: string,
  config: AnalyticsConfig,
  currentYear: number,
  catalog: MetricDefinition[],
  metricOverride?: string,
): string {
  const card = config.summaryCards.find(entry => entry.id === cardId);
  if (!card) return cardId;
  if (metricOverride && !isPresetSummaryCard(cardId)) {
    const metric = getMetricByField(catalog, metricOverride);
    if (metric) return `Average ${metric.label}`;
  }
  return typeof card.label === 'function' ? card.label(currentYear) : card.label;
}
